import { CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowRightIcon from '@material-ui/icons/ArrowRight';
import Check from '@material-ui/icons/Check';
import TreeItem from '@material-ui/lab/TreeItem';
import TreeView from '@material-ui/lab/TreeView';
import PropTypes from 'prop-types';
import React from 'react';
import HighlightedTypography from '../../../components/HighlightedTypography';
import IconWithTooltip from '../../../components/IconWithTooltip';

const useTreeItemStyles = makeStyles(theme => ({
  root: {
    color: theme.palette.text.secondary,
    '&:hover > $content': {
      backgroundColor: theme.palette.action.hover
    },
    '&:focus > $content, &$selected > $content': {
      backgroundColor: `var(--tree-view-bg-color, ${theme.palette.grey[400]})`,
      color: 'var(--tree-view-color)'
    },
    '&:focus > $content $label, &:hover > $content $label, &$selected > $content $label': {
      backgroundColor: 'transparent'
    }
  },
  content: {
    color: theme.palette.text.secondary,
    borderTopRightRadius: theme.spacing(2),
    borderBottomRightRadius: theme.spacing(2),
    paddingRight: theme.spacing(1),
    fontWeight: theme.typography.fontWeightMedium,
    '$expanded > &': {
      fontWeight: theme.typography.fontWeightRegular
    }
  },
  group: {
    marginLeft: 0,
    '& $content': {
      paddingLeft: theme.spacing(2)
    }
  },
  expanded: {},
  selected: {},
  disabled: {
    color: theme.palette.text.disabled, // Cambia el color del texto para que parezca deshabilitado
    opacity: 0.5 // Puedes ajustar la opacidad para hacerlo aún más evidente
  },
  label: {
    fontWeight: 'inherit',
    color: 'inherit'
  },
  labelRoot: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1, 0)
  },
  labelIcon: {
    marginRight: theme.spacing(1)
  },
  labelText: {
    fontWeight: 'inherit'
    // flexGrow: 1
  },
  labelInfo: {
    marginLeft: theme.spacing(1)
  }
  // // Generar clases dinámicamente para múltiples niveles
  // ...[...Array(10).keys()].reduce((acc, level) => {
  //   acc[`level${level + 1}`] = {
  //     marginLeft: 0,
  //     '& $content': {
  //       paddingLeft: theme.spacing((level + 1) * 2)
  //     }
  //   };
  //   return acc;
  // }, {})
}));

const StyledTreeItem = props => {
  const classes = useTreeItemStyles();
  const {
    labelText,
    labelIcon: LabelIcon,
    labelInfo,
    isLoading,
    disabled,
    color,
    bgColor,
    level,
    highlight,
    readOnly,
    ...other
  } = props;

  let Icon;
  if (isLoading) {
    Icon = <CircularProgress size={8} />;
  }

  return (
    <>
      <TreeItem
        icon={Icon}
        label={
          <div className={classes.labelRoot}>
            {LabelIcon && (
              <LabelIcon color="inherit" className={classes.labelIcon} />
            )}
            <HighlightedTypography
              variant="body2"
              className={classes.labelText}
              highlight={highlight}
            >
              {labelText}
            </HighlightedTypography>
            {labelInfo && (
              <div className={classes.labelInfo}>
                <IconWithTooltip title={labelInfo} fontSize="small" />
              </div>
            )}
          </div>
        }
        style={{
          '--tree-view-color': !readOnly && color,
          '--tree-view-bg-color': !readOnly && bgColor,
          ...(disabled || readOnly
            ? { pointerEvents: 'none', cursor: 'none' }
            : {})
        }}
        classes={{
          root: classes.root,
          content: classes.content,
          expanded: classes.expanded,
          selected: classes.selected,
          group: classes[`level${level}`],
          label: disabled ? classes.disabled : classes.label,
          iconContainer: disabled ? classes.disabled : classes.label
        }}
        {...other}
      />
    </>
  );
};

StyledTreeItem.propTypes = {
  bgColor: PropTypes.string,
  color: PropTypes.string,
  labelIcon: PropTypes.elementType,
  labelInfo: PropTypes.string,
  labelText: PropTypes.string.isRequired
};

const useStyles = makeStyles({
  root: {
    // height: 264,
    flexGrow: 1
    // maxWidth: 400
  }
});

const fixEmptyChildren = cap => {
  const copy = { ...cap };
  if (!copy.isLeaf && copy.children?.length === 0) {
    copy.children = [
      {
        name: `Cargando...`,
        id: `${cap.id}-empty`,
        isLoading: cap.isLoading,
        nodeId: `${cap.id}-empty`,
        disabled: true
      }
    ];
    delete copy.isLoading;
  }
  return copy;
};

const PartidaArancelariaView = ({
  expanded,
  readOnly,
  data,
  highlight,
  ...props
}) => {
  const classes = useStyles();
  const renderTreeItem = (node, level = 1) => {
    const tieneHijos = Array.isArray(node.children);
    return (
      <StyledTreeItem
        key={node.id}
        nodeId={node.id}
        labelText={node.name}
        labelInfo={node.info}
        isLoading={node.isLoading}
        disabled={node.disabled}
        color="#3c8039"
        bgColor="#e6f4ea"
        highlight={highlight}
        level={level}
        readOnly={readOnly}
      >
        {tieneHijos
          ? node.children
              .map(fixEmptyChildren)
              .map(child => renderTreeItem(child, level + 1))
          : null}
      </StyledTreeItem>
    );
  };

  return (
    <TreeView
      className={classes.root}
      children={data.map(fixEmptyChildren).map(item => renderTreeItem(item))}
      defaultCollapseIcon={<ArrowDropDownIcon style={{ fontSize: 24 }} />}
      defaultExpandIcon={<ArrowRightIcon style={{ fontSize: 24 }} />}
      // defaultEndIcon={<div style={{ width: 24 }} />}
      defaultEndIcon={<Check style={{ fontSize: 24 }} />}
      // defaultParentIcon={<Folder />}
      expanded={expanded}
      {...props}
    />
  );
};

export default PartidaArancelariaView;
